import { useId } from "react";

type SparkValue = number | null | undefined;

type SparkProps = {
  data: SparkValue[] | null | undefined;
  color?: string;
  width?: number;
  height?: number;
};

/**
 * Inline sparkline. Auto-scales the vertical axis to the data's own
 * min/max so small-magnitude metrics (CPA $0.49, ROAS 1.02) show the
 * same amount of day-to-day wiggle as big ones (spend $12,000).
 *
 * `null` / `undefined` entries are gaps — neither counted in the
 * min/max nor drawn. This matters for rate metrics (CPA, ROAS) where
 * a day with no installs has no meaningful value; treating that as
 * `0` used to hammer the baseline flat and crowd real values into a
 * thin band at the top. Only an actual reported zero (all values are
 * exactly 0) still renders as a true flat line, per user spec.
 */
export function Spark({
  data,
  color = "var(--c-accent-500)",
  width = 80,
  height = 28,
}: SparkProps) {
  const gradientId = useId();

  if (!data || data.length === 0) return null;

  // Collect indexes of real values so we can still place them on the
  // correct x (proportional to their position in the original series).
  const real: { i: number; v: number }[] = [];
  for (let i = 0; i < data.length; i++) {
    const v = data[i];
    if (v != null && Number.isFinite(v)) real.push({ i, v });
  }
  if (real.length === 0) return null;

  const values = real.map((r) => r.v);
  const min = Math.min(...values);
  const max = Math.max(...values);

  // Absolute flat line only when every real value is the same. A tiny
  // non-zero range still gets the full vertical span — that's the
  // whole point: see the wiggle, not assume 0 as the baseline.
  const range = max - min;
  const flat = range === 0;

  // When we have a single real point, draw a dot/short line segment
  // at mid-height so it doesn't collapse into the top edge.
  const denomX = data.length > 1 ? data.length - 1 : 1;
  const points = real.map(({ i, v }) => {
    const x = (i / denomX) * width;
    const y = flat
      ? height / 2
      : height - ((v - min) / range) * (height - 4) - 2;
    return [x, y];
  });

  const stroke = points
    .map((p, i) => `${i === 0 ? "M" : "L"}${p[0].toFixed(1)} ${p[1].toFixed(1)}`)
    .join(" ");

  // For the gradient fill we anchor to the bottom — visually the
  // filled area below the line "sits on the floor" regardless of the
  // numeric min.
  const first = points[0]!;
  const last = points[points.length - 1]!;
  const fill = `M${first[0].toFixed(1)} ${height} ${stroke
    .replace(/^M/, "L")
    .replace(/^L/, "L")} L${last[0].toFixed(1)} ${height} Z`;

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
      <defs>
        <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={fill} fill={`url(#${gradientId})`} stroke="none" />
      <path d={stroke} fill="none" stroke={color} strokeWidth="1.5" />
    </svg>
  );
}
